// src/components/Whatsapp/MessageNode.js
import { React, useState, useEffect, useRef } from "react";
import {
  Typography,
  Box,
  Paper,
  Card,
  Avatar,
  Modal,
  Backdrop,
  Stack,
  Dialog,
  DialogContent,
} from "@mui/material";
import FormPreview from "../Forms/Components/FormPreview";
import MessageIcon from "@mui/icons-material/Message";
import PhoneIcon from "@mui/icons-material/Phone";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReplyIcon from "@mui/icons-material/Reply";
import CloseIcon from "@mui/icons-material/Close";
import ListIcon from "@mui/icons-material/List";
import PDFPreview from "./PDFPreview";
import waBackgroundImg from "../../assets/images/wa_bg.jpg";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import IconButton from "@mui/material/IconButton";
import {
  ArrowBack,
  ArrowLeft,
  AttachFile,
  CameraAlt,
  ContentCopy,
  EmojiEmotions,
  Image,
  KeyboardBackspace,
  LocationCity,
  LocationOn,
  Mic,
  MoreVert,
  Verified,
  WhatsApp,
} from "@mui/icons-material";
import {
  FORMS_FORMS,
  GET_DASHBOARD_WABA_STATUS,
  SETTINGS_DASHBOARD_ENDPOINT,
  WA_BUSINESS_ACCOUNT_DATA,
} from "../../config/urls";
import { useAuth } from "../../hooks/AuthProvider";
import axios from "axios";

const convertMarkdownToHtml = (content) => {
  if (!content) return "";

  let html = content;

  // Convert bold (asterisks)
  html = html.replace(/\*(?! )([^\n*]+)(?<! )\*/g, "<strong>$1</strong>");

  // Convert italic (underscores)
  html = html.replace(/_(?! )([^\n_]+)(?<! )_/g, "<em>$1</em>");

  // Convert strikethrough (tildes)
  html = html.replace(/~(?! )([^\n~]+)(?<! )~/g, "<s>$1</s>");

  // Convert monospace (backticks)
  // html = html.replace(/```([^`]+)```/g, '<code>$1</code>');
  html = html.replace(
    /```([^`]+)```/g,
    '<span style="font-family: monospace; background-color: #f0f0f0; padding: 2px 4px; border-radius: 3px; font-size: 0.9em;">$1</span>',
  );

  // Convert inline code (single backtick)
  // html = html.replace(/`(?! )([^\n`]+)(?<! )`/g, '<code>$1</code>');
  html = html.replace(
    /`(?! )([^\n`]+)(?<! )`/g,
    '<span style="font-family: monospace; background-color: #f0f0f0; padding: 2px 4px; border-radius: 3px; font-size: 0.9em;">$1</span>',
  );

  // covert quotes
  html = html.replace(
    /^\s*>\s*(.+)$/gm,
    '<blockquote style="padding: 8px 12px; border-left: 4px solid #ccc; background-color: #f9f9f9; margin: 8px 0;">$1</blockquote>',
  );

  // Convert bulleted lists
  const bulletListRegex = /^(\* .+)$/gm;
  if (bulletListRegex.test(html)) {
    let bulletMatches = html.match(bulletListRegex);
    if (bulletMatches) {
      // Create a string to replace all bullet items with
      let replacementHtml = "<ul>";
      bulletMatches.forEach((item) => {
        replacementHtml += `<li>${item.substring(2).trim()}</li>`;
      });
      replacementHtml += "</ul>";

      // Create a regex that matches all consecutive bullet items
      const consecutiveBulletItems = bulletMatches
        .map(
          (item) => item.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), // Escape regex special chars
        )
        .join("\\n");

      // Replace the entire block with the HTML list
      const blockRegex = new RegExp(consecutiveBulletItems, "g");
      html = html.replace(blockRegex, replacementHtml);
    }
  }

  // Convert hyphen lists
  const hyphenListRegex = /^(- .+)$/gm;
  if (hyphenListRegex.test(html)) {
    let hyphenMatches = html.match(hyphenListRegex);
    if (hyphenMatches) {
      // Create a string to replace all hyphen items with
      let replacementHtml = "<ul>";
      hyphenMatches.forEach((item) => {
        replacementHtml += `<li>${item.substring(2).trim()}</li>`;
      });
      replacementHtml += "</ul>";

      // Create a regex that matches all consecutive hyphen items
      const consecutiveHyphenItems = hyphenMatches
        .map(
          (item) => item.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), // Escape regex special chars
        )
        .join("\\n");

      // Replace the entire block with the HTML list
      const blockRegex = new RegExp(consecutiveHyphenItems, "g");
      html = html.replace(blockRegex, replacementHtml);
    }
  }

  // Convert numbered lists
  const numberedListRegex = /^(\d+\. .+)$/gm;
  if (numberedListRegex.test(html)) {
    let numberedMatches = html.match(numberedListRegex);
    if (numberedMatches) {
      // Create a string to replace all numbered items with
      let replacementHtml = "<ol>";
      numberedMatches.forEach((item) => {
        const content = item.substring(item.indexOf(".") + 2).trim();
        replacementHtml += `<li>${content}</li>`;
      });
      replacementHtml += "</ol>";

      // Create a regex that matches all consecutive numbered items
      const consecutiveNumberedItems = numberedMatches
        .map(
          (item) => item.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), // Escape regex special chars
        )
        .join("\\n");

      // Replace the entire block with the HTML list
      const blockRegex = new RegExp(consecutiveNumberedItems, "g");
      html = html.replace(blockRegex, replacementHtml);
    }
  }

  // Convert quotes
  const quoteRegex = /^> (.+)$/gm;
  if (quoteRegex.test(html)) {
    let quoteMatches = html.match(quoteRegex);
    if (quoteMatches) {
      // Create a div with WhatsApp-style quote formatting
      let quoteContent = "";
      quoteMatches.forEach((item) => {
        quoteContent += item.substring(2).trim() + "<br>";
      });

      // Remove the last <br> if it exists
      if (quoteContent.endsWith("<br>")) {
        quoteContent = quoteContent.substring(0, quoteContent.length - 4);
      }

      const quoteHtml = `<div style="
                padding: 8px 12px; 
                border-radius: 5px; 
                margin: 5px 0; 
                position: relative;
            ">${quoteContent}</div>`;

      // Create a regex that matches all consecutive quote items
      const consecutiveQuoteItems = quoteMatches
        .map(
          (item) => item.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), // Escape regex special chars
        )
        .join("\\n");

      // Replace the entire block with the WhatsApp-style quote
      const blockRegex = new RegExp(consecutiveQuoteItems, "g");
      html = html.replace(blockRegex, quoteHtml);
    }
  }

  // Convert newlines to <br> tags
  // html = html.replace(/\n/g, '<br>');

  // Normalize Windows line endings
  html = html.replace(/\r\n/g, "\n");

  // Split into lines
  const lines = html.split("\n");

  // Convert lines to HTML with correct spacing
  html = lines
    .map((line) => {
      if (line === "") {
        return "<br>"; // one <br> for blank line
      }
      return `${line}<br>`; // one <br> for actual line, not two
    })
    .join("");

  return html;
};

const MessageNode = (props) => {
  const headerType = props.headerType;
  const headerText = props.headerText;
  const headerFile = props.headerFile;
  const body = props.body;
  const sampleBodyFields = props.sampleBodyFields;
  const sampleHeaderFields = props.sampleHeaderFields; // Added for template analytics
  const footer = props.footer;
  const buttons = props.buttons;
  const mode = props.mode;
  const productCount = Number.isInteger(props?.productCount) ? props.productCount : 2; // Product Count for MPM Templates
  const thumbnailImage = props.thumbnailImage; // Selected thumbnail product image for MPM/SPM Templates
  const productName = props.productName; // Selected product name for SPM Templates
  const allButtons =
    props.marketingOption === "catalog"
      ? [
          {
            type: "catalog",
            data: {
              buttonText: "View Catalog",
            },
          },
        ]
      : props.allButtons;
  // New prop to determine if we should show sample values (used by TemplateAnalyticsTab)
  const showSampleValues = props.showSampleValues || false;
  const carouselType = props.carouselType || "image";
  const location = props.location
    ? typeof props.location === "string"
      ? JSON.parse(props.location)
      : props.location
    : null;

  const [showAllButtons, setShowAllButtons] = useState(false);
  const [mediaUrl, setMediaUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [userProfileData, setUserProfileData] = useState({
    name: "",
    email: "",
    photo: "",
    plan: "",
    userCount: "",
    businessName: "",
    phone: "",
    whatsappBusinessApiStatus: "disconnected",
  });
  const [loading, setLoading] = useState({ profile: false });
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const carouselRef = useRef(null);
  const [cardMediaUrls, setCardMediaUrls] = useState({});

  const [businessData, setBusinessData] = useState({
    user_name: "",
    user_email: "",
    business_name: "",
    business_wa_number: "",
  });

  const tenant = localStorage.getItem("tenant");
  const { getValidToken } = useAuth();

  // WhatsApp Form button: tapping it in the preview walks through the form.
  const [formPreviewJson, setFormPreviewJson] = useState(null);
  const openFormPreview = async (formId) => {
    try {
      const token = await getValidToken();
      const res = await fetch(`${FORMS_FORMS}${formId}/`, {
        headers: { Authorization: `Bearer ${token}`, "X-Tenant-Id": tenant },
      });
      if (res.ok) setFormPreviewJson((await res.json()).form_json);
    } catch (e) {
      console.error("Could not load the form preview", e);
    }
  };

  const fetchBusinessData = async () => {
    const token = await getValidToken();
    const fetchURL = SETTINGS_DASHBOARD_ENDPOINT;
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-ID": tenant,
    };

    const response = await fetch(fetchURL, { headers });
    const responseData = await response.json();

    setBusinessData(responseData);
  };

  const fetchBusinessDataTwo = async () => {
    try {
      setLoading((prev) => ({ ...prev, business: true }));

      const headers = {
        "X-Tenant-ID": tenant,
      };
      const response = await axios.get(WA_BUSINESS_ACCOUNT_DATA, { headers });
      const data = response?.data;
      console.log("Business Datas Response:", data);

      if (data?.status === "success" && data?.business) {
        const business = data.business;
        setUserProfileData((prev) => ({
          ...prev,
          businessName: business?.name || "",
        }));
      }
    } catch (error) {
      console.error("Error fetching business data:", error);
    } finally {
      setLoading((prev) => ({ ...prev, business: false }));
    }
  };

  useEffect(() => {
    fetchBusinessData();
    fetchBusinessDataTwo();
  }, []);

  // For carousel template - navigate between cards
  const handleCardNavigation = (direction) => {
    if (!props.carouselCards) return;

    const cards =
      typeof props.carouselCards === "string"
        ? JSON.parse(props.carouselCards)
        : props.carouselCards;

    // Pause any currently playing video before navigation
    const currentVideoId = `carousel-video-${currentCardIndex}`;
    const currentVideo = document.getElementById(currentVideoId);
    if (currentVideo) {
      currentVideo.pause();
    }

    if (direction === "next" && currentCardIndex < cards.length - 1) {
      setCurrentCardIndex((prev) => prev + 1);
    } else if (direction === "prev" && currentCardIndex > 0) {
      setCurrentCardIndex((prev) => prev - 1);
    }
  };

  // For carousel template - get file for current card
  function getCardFileForIndex(index) {
    // First try to get the file from props.carouselCards if it's parsed
    const carouselCards =
      typeof props.carouselCards === "string"
        ? JSON.parse(props.carouselCards)
        : props.carouselCards;

    // Make sure we have valid cards and the requested index exists
    if (
      !carouselCards ||
      !Array.isArray(carouselCards) ||
      !carouselCards[index]
    ) {
      console.warn(`No card found at index ${index}`);
      return null;
    }

    // Get the current card
    const currentCard = carouselCards[index];

    // Try to get file from headerFile property in the card
    if (currentCard && currentCard.headerFile) {
      return currentCard.headerFile;
    }

    // Try to get file from props using cardFile_INDEX naming
    const cardFileKey = `cardFile_${index}`;
    if (props[cardFileKey]) {
      // Handle both string URLs and File objects
      return props[cardFileKey];
    }

    // Determine if we should be looking for a video or image file
    const isVideoCard =
      props.carouselType === "video" ||
      (currentCard.headerType &&
        currentCard.headerType.toLowerCase() === "video");

    // Match file by type (image or video)
    if (isVideoCard) {
      // Look for video-specific properties
      if (props[`cardVideo_${index}`]) {
        return props[`cardVideo_${index}`];
      }
    } else {
      // Look for image-specific properties
      if (props[`cardImage_${index}`]) {
        return props[`cardImage_${index}`];
      }
    }

    console.warn(`No media file found for card ${index}`);
    return null;
  }

  // Fetch media with headers when headerFile changes
  useEffect(() => {
    // Skip if no header file or it's not a string URL
    if (!headerFile || typeof headerFile !== "string") return;

    const tenant = localStorage.getItem("tenant");
    const headers = {
      "X-Tenant-Id": tenant,
    };

    const fetchMediaWithHeaders = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(headerFile, { headers });
        if (!response.ok) throw new Error("Failed to fetch media");

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        setMediaUrl(objectUrl);
      } catch (error) {
        console.error("Error fetching media:", error);
        // Fallback to direct URL if fetch fails
        setMediaUrl(headerFile);
      } finally {
        setIsLoading(false);
      }
    };

    // If headerType is a media type, fetch it
    if (
      headerType === "image" ||
      headerType === "video" ||
      headerType === "document"
    ) {
      fetchMediaWithHeaders();
    }

    // Cleanup function
    return () => {
      if (mediaUrl && mediaUrl.startsWith("blob:")) {
        URL.revokeObjectURL(mediaUrl);
      }
    };
  }, [headerFile, headerType]);

  useEffect(() => {
    if (props.marketingOption === "carousel") {
      // console.group("Carousel Debug Info");
      // console.log("Current card index:", currentCardIndex);
      // console.log("Card media URLs:", cardMediaUrls);
      // console.log("Carousel type:", props.carouselType || "image (default)");

      const parsedCards =
        typeof props.carouselCards === "string"
          ? JSON.parse(props.carouselCards)
          : props.carouselCards;

      if (parsedCards && Array.isArray(parsedCards)) {
        console.log("Total cards:", parsedCards.length);

        if (parsedCards[currentCardIndex]) {
          const currentCard = parsedCards[currentCardIndex];
          console.log(
            "Current card file:",
            getCardFileForIndex(currentCardIndex),
          );
          console.log(
            "Current card type:",
            currentCard.headerType || props.carouselType || "image (default)",
          );
          console.log(
            "Is video card:",
            props.carouselType === "video" ||
              (currentCard.headerType &&
                currentCard.headerType.toLowerCase() === "video"),
          );
        }
      }

      console.groupEnd();
    }
  }, [
    props.marketingOption,
    props.carouselCards,
    currentCardIndex,
    cardMediaUrls,
    props.carouselType,
  ]);

  useEffect(() => {
    // This effect runs when currentCardIndex changes
    if (props.marketingOption === "carousel") {
      const carouselCards =
        typeof props.carouselCards === "string"
          ? JSON.parse(props.carouselCards)
          : props.carouselCards;

      if (!carouselCards || !Array.isArray(carouselCards)) return;

      // Get the file for the current card
      const cardFileObj = getCardFileForIndex(currentCardIndex);

      // Get the current card to check if it's a video
      const currentCard = carouselCards[currentCardIndex];
      const isVideoCard =
        props.carouselType === "video" ||
        (currentCard &&
          currentCard.headerType &&
          currentCard.headerType.toLowerCase() === "video");

      console.log(
        `Switching to card ${currentCardIndex}, isVideo: ${isVideoCard}, file:`,
        cardFileObj,
      );

      // If we need to load a new card and don't have it in our cache - Removed the mediaURL check
      if (cardFileObj) {
        // Set loading state
        setIsLoading(true);

        const loadCardMedia = async () => {
          try {
            if (typeof cardFileObj !== "string") {
              // If it's a File object, create a blob URL
              const objectUrl = URL.createObjectURL(cardFileObj);
              setCardMediaUrls((prev) => ({
                ...prev,
                [currentCardIndex]: objectUrl,
              }));
            } else {
              // If it's a string URL, fetch it
              const tenant = localStorage.getItem("tenant");
              const headers = {
                "X-Tenant-Id": tenant,
              };

              const response = await fetch(cardFileObj, { headers });
              if (!response.ok)
                throw new Error("Failed to fetch carousel media");

              const blob = await response.blob();
              const objectUrl = URL.createObjectURL(blob);

              setCardMediaUrls((prev) => ({
                ...prev,
                [currentCardIndex]: objectUrl,
              }));
            }
          } catch (error) {
            console.error(
              `Error fetching media for card ${currentCardIndex}:`,
              error,
            );
            // Fallback to direct URL
            if (typeof cardFileObj === "string") {
              setCardMediaUrls((prev) => ({
                ...prev,
                [currentCardIndex]: cardFileObj,
              }));
            }
          } finally {
            setIsLoading(false);
          }
        };

        loadCardMedia();
      } else {
        setIsLoading(false);
      }
    }
  }, [
    currentCardIndex,
    props.marketingOption,
    (() => {
      const carouselCards =
        typeof props.carouselCards === "string"
          ? JSON.parse(props.carouselCards)
          : props.carouselCards;
      if (!carouselCards || !Array.isArray(carouselCards)) return undefined;
      const currentCard = carouselCards[currentCardIndex];
      return currentCard && currentCard.headerFile;
    })(),
  ]); // Remove currentCardIndex dependency

  // This effect handles video cleanup when cards change
  useEffect(() => {
    if (props.marketingOption === "carousel") {
      // Get all video elements with the carousel video prefix
      const videoElements = document.querySelectorAll(
        '[id^="carousel-video-"]',
      );

      // Return cleanup function to pause videos when unmounting or when currentCardIndex changes
      return () => {
        videoElements.forEach((video) => {
          if (video && video.id !== `carousel-video-${currentCardIndex}`) {
            video.pause();
          }
        });
      };
    }
  }, [currentCardIndex, props.marketingOption]);

  // Function to replace placeholders with sample data (for template analytics)
  const replacePlaceholders = (text, sampleFields) => {
    if (!text || !sampleFields || !Array.isArray(sampleFields)) return text;

    let replacedText = text;

    // Escape special regex characters in case they exist in the text
    for (let i = 0; i < sampleFields.length; i++) {
      // The pattern needs to match exactly {{1}}, {{2}}, etc.
      const pattern = "{{" + (i + 1) + "}}";

      // Simple string replacement
      replacedText = replacedText.replaceAll(
        pattern,
        `{{${sampleFields[i].value || i + 1}}}` || "",
      );
    }

    return replacedText;
  };

  // Apply sample replacements if showSampleValues is true
  const displayHeaderText = replacePlaceholders(headerText, sampleHeaderFields);
  const displayBodyText = replacePlaceholders(body, sampleBodyFields);

  // New function for the slide-up modal
  const createSlideUpModal = ({ open, onClose, buttons }) => {
    if (!open) return null;

    return (
      <>
        {/* Backdrop */}
        <Box
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1,
            borderRadius: "20px",
          }}
        />
        {/* Sliding Content */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: "100%",
            bgcolor: "background.paper",
            borderRadius: "20px",
            boxShadow: 24,
            py: 1,
            px: 2,
            zIndex: 2,
            animation: "slideUp 0.3s ease-out",
            "@keyframes slideUp": {
              from: { transform: "translate(-50%, 100%)" },
              to: { transform: "translate(-50%, 0)" },
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="h6">Options</Typography>
            <CloseIcon
              onClick={onClose}
              sx={{
                cursor: "pointer",
                "&:hover": { opacity: 0.7 },
              }}
            />
          </Box>

          <Box
            sx={{
              maxHeight: "300px",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
              "::-webkit-scrollbar": {
                width: "8px",
              },
              "::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "4px",
              },
              "::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "4px",
                "&:hover": {
                  background: "#555",
                },
              },
            }}
          >
            {buttons && buttons.map((button, index) => buttonPreview(button))}
          </Box>
        </Box>
      </>
    );
  };

  function getButtonIcon(type) {
    switch (type) {
      case "call-phone-number":
        return <PhoneIcon sx={{ fontSize: 16, color: "primary.main" }} />;
      case "visit-website":
        return <OpenInNewIcon sx={{ fontSize: 16, color: "primary.main" }} />;
      case "marketing-opt-out":
        return <ReplyIcon sx={{ fontSize: 16, color: "primary.main" }} />;
      case "custom":
      case "whatsapp-form":
        return <ReplyIcon sx={{ fontSize: 16, color: "primary.main" }} />;
      case "copy-code":
        return <ContentCopy sx={{ fontSize: 16, color: "primary.main" }} />;
      case "autofill":
        return <OpenInNewIcon sx={{ fontSize: 16, color: "primary.main" }} />;
      default:
        return null;
    }
  }

  const styles = {
    image: {
      width: "100%",
      height: "auto",
      display: "block",
      borderRadius: 2,
      cursor: "pointer",
    },
  };

  function headerPreview(header) {
    const titleStyle = {
      padding: "4px 12px",
      height: "28px",
      background: "#F5F5F5",
      borderRadius: "4px",
      marginBottom: "3px",
    };

    const renderImage = (file, onRemove) => {
      if (!file) return null;

      if (isLoading) {
        return <Box sx={{ textAlign: "center", mt: 2 }}>Loading image...</Box>;
      }

      const imageSource =
        typeof file === "string" ? mediaUrl || file : URL.createObjectURL(file);

      return (
        <Box
          component="img"
          src={imageSource}
          alt="Preview"
          sx={styles.image}
        />
      );
    };

    const renderGIF = (file, onRemove) => {
      if (!file) return null;

      if (isLoading) {
        return <Box sx={{ textAlign: "center", mt: 2 }}>Loading GIF...</Box>;
      }

      const gifSource =
        typeof file === "string" ? mediaUrl || file : URL.createObjectURL(file);

      return (
        <Box
          component="img"
          src={gifSource}
          alt="Preview"
          sx={styles.image}
        />
      );
    };

    const renderVideo = (videoFile) => {
      if (!videoFile) return null;

      if (isLoading) {
        return <div>Loading video...</div>;
      }

      // Use the mediaUrl from state if it's a string URL, otherwise use file directly
      const videoSource =
        typeof videoFile === "string"
          ? mediaUrl || videoFile
          : URL.createObjectURL(videoFile);

      return (
        <video
          key={videoSource}
          width="100%"
          controls
          autoPlay
          muted
          loop
          style={{ borderRadius: 8 }}
        >
          <source src={videoSource} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      );
    };

    const renderPDF = (pdfFile) => {
      if (!pdfFile) return null;

      if (isLoading) {
        return <div>Loading PDF...</div>;
      }

      // For PDFPreview, pass either the mediaUrl or the file object
      const pdfSource =
        typeof pdfFile === "string" ? mediaUrl || pdfFile : pdfFile;

      return <PDFPreview pdfFile={pdfSource} />;
    };

    const renderLocation = () => {
      return location ? (
        <Box
          sx={{ width: "100%", flexDirection: "column", alignItems: "center" }}
        >
          <Box
            sx={{
              width: "100%",
              height: 150,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgb(230, 230, 230)",
              borderRadius: "8px",
            }}
          >
            <LocationOn fontSize="large" sx={{ color: "red" }} />
            <Typography sx={{ fontSize: "12px" }}>
              ({location.latitude}°, {location.longitude}°)
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontWeight: "bold" }}>{location.name}</Typography>
            <Typography sx={{ fontSize: "12px" }}>
              {location.address}
            </Typography>
          </Box>
        </Box>
      ) : (
        <Typography>No location found</Typography>
      );
    };

    return (
      <>
        {header.format === "text" ? (
          <Typography sx={{ fontWeight: "bold", wordBreak: "break-all" }}>
            {header.str}
          </Typography>
        ) : header.format === "image" ? (
          renderImage(header.file)
        ) : header.format === "gif" ? (
          renderGIF(header.file)
        ) : header.format === "video" ? (
          renderVideo(header.file)
        ) : header.format === "document" ? (
          renderPDF(header.file)
        ) : header.format === "location" ? (
          renderLocation()
        ) : null}
      </>
    );
  }

  function bodyPreview(text) {
    // Use markdown conversion only when not in sample mode
    const htmlContent = convertMarkdownToHtml(text);

    return (
      <>
        <div
          style={{
            whiteSpace: "pre-wrap",
            padding: "8px 0", // Add vertical padding
            lineHeight: "1.5", // Improve line height
            wordBreak: "break-all",
          }}
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
      </>
    );
  }

  function footerPreview(text) {
    return (
      <>
        <Typography sx={{ fontWeight: 300, color: "gray" }}>{text}</Typography>
      </>
    );
  }

  function buttonPreview(buttonData) {
    // Adjust button width based on showSampleValues
    const buttonStyle = {
      width: "100%",
      height: "30px",
      minHeight: "30px",
      marginBottom: "8px",
      backgroundColor: "#FFFFFF",
      padding: "5.34103px 0px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
    };
    const isFormButton =
      buttonData.type === "whatsapp-form" && buttonData.data.formId;
    return (
      <Card
        variant="elevation"
        sx={buttonStyle}
        onClick={isFormButton ? () => openFormPreview(buttonData.data.formId) : undefined}
      >
        {getButtonIcon(buttonData.type)}
        <Typography
          align="center"
          color={"#007DFF"}
          fontSize={"14px"}
          fontWeight={400}
        >
          {buttonData.data.buttonText}
        </Typography>
      </Card>
    );
  }

  const handlePaginationClick = (index) => {
    // Pause any currently playing video before navigation
    const currentVideoId = `carousel-video-${currentCardIndex}`;
    const currentVideo = document.getElementById(currentVideoId);
    if (currentVideo) {
      currentVideo.pause();
    }

    // Switch to the selected card
    setCurrentCardIndex(index);
  };

  function carouselPreview() {
    // Parse carousel data if it's a string
    const carouselCards =
      typeof props.carouselCards === "string"
        ? JSON.parse(props.carouselCards)
        : props.carouselCards;

    const carouselMainBody = props.carouselMainBody || "";
    const carouselMainBodySampleFields = props.carouselMainBodySampleFields;

    const displayCarouselMainBody = replacePlaceholders(
      carouselMainBody,
      carouselMainBodySampleFields,
    );

    if (!carouselCards || carouselCards.length === 0) {
      return <Typography>No carousel cards available</Typography>;
    }

    // Get the current card to display
    const currentCard = carouselCards[currentCardIndex];

    // Get the card file for the current card index
    let cardFileObj = currentCard.headerFile || null;
    if (!cardFileObj && props[`cardFile_${currentCardIndex}`]) {
      cardFileObj = props[`cardFile_${currentCardIndex}`];
    }

    // Determine if the current card is a video or image
    // Use carouselType from props first, then fall back to checking the card's headerType property
    // Determine card type - more robust checking
    const isVideoCard = (() => {
      // 1. First check if the global carousel type is set to video
      if (props.carouselType === "video") {
        return true;
      }

      // 2. Check the current card's headerType property
      if (currentCard && currentCard.headerType) {
        return currentCard.headerType.toLowerCase() === "video";
      }

      // 3. Check if the file object has a video MIME type
      if (cardFileObj && typeof cardFileObj === "object" && cardFileObj.type) {
        return cardFileObj.type.startsWith("video/");
      }

      // 4. Check file extension if it's a string URL
      if (cardFileObj && typeof cardFileObj === "string") {
        const fileExtension = cardFileObj.split(".").pop().toLowerCase();
        return ["mp4", "webm", "ogg", "mov"].includes(fileExtension);
      }

      // Default to the global carousel type or false
      return false;
    })();

    function renderImage(file) {
      if (!file) return null;

      if (isLoading) {
        return (
          <Box
            sx={{
              textAlign: "center",
              mt: 2,
              height: "200px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Loading image...
          </Box>
        );
      }

      // Use cached media URL if available, otherwise use file
      const imageSource =
        cardMediaUrls[currentCardIndex] ||
        (typeof file === "string" ? file : URL.createObjectURL(file));

      // Create a placeholder for when image fails to load
      const placeholderImage =
        "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2VlZWVlZSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTQiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIGZpbGw9IiM5OTkiPkltYWdlIG5vdCBhdmFpbGFibGU8L3RleHQ+PC9zdmc+";

      return (
        <Box
          component="img"
          src={imageSource}
          alt={`Card ${currentCardIndex + 1}`}
          sx={{
            ...styles.image,
            minHeight: "200px", // Add min height
            objectFit: "contain", // Ensure image is displayed properly
            backgroundColor: "#f5f5f5", // Light background for transparent images
          }}
          onError={(e) => {
            console.error(`Error loading carousel image: ${imageSource}`);
            e.target.onerror = null; // Prevent infinite error loop
            e.target.src = placeholderImage;
          }}
        />
      );
    }

    function renderVideo(file) {
      if (!file) return null;

      if (isLoading) {
        return (
          <Box
            sx={{
              textAlign: "center",
              mt: 2,
              height: "200px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Loading video...
          </Box>
        );
      }

      // Use cached media URL if available, otherwise use file directly
      const videoSource =
        cardMediaUrls[currentCardIndex] ||
        (typeof file === "string" ? file : URL.createObjectURL(file));

      // Use a key that changes when the videoSource changes to force re-mount
      const videoId = `carousel-video-${currentCardIndex}`;
      const videoKey = `${videoId}-${videoSource}`;

      return (
        <Box
          sx={{ width: "100%", minHeight: "200px", backgroundColor: "#000" }}
        >
          <video
            id={videoId}
            key={videoKey} // Force re-mount when source changes
            width="100%"
            height="auto"
            controls
            playsInline // Better for mobile
            muted
            style={{
              borderRadius: "8px",
              maxHeight: "300px",
              objectFit: "contain",
            }}
          >
            <source src={videoSource} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </Box>
      );
    }

    return (
      <Box sx={{ width: "100%", position: "relative" }}>
        {/* Main body message at the top */}
        <Card
          variant="elevation"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            width: "100%",
            backgroundColor: "#FFFFFF",
            padding: showSampleValues ? "7.12px" : "10px",
            mb: 1,
            "& p": {
              lineHeight: "1.5",
            },
          }}
        >
          {bodyPreview(displayCarouselMainBody)}
        </Card>

        {/* Card display with navigation controls */}
        <Box
          ref={carouselRef}
          sx={{
            position: "relative",
            boxShadow: "0 2px 4px rgba(245, 235, 235, 0.1)",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          {/* Left navigation arrow */}
          {currentCardIndex > 0 && (
            <IconButton
              onClick={() => handleCardNavigation("prev")}
              sx={{
                position: "absolute",
                left: 0,
                top: "50%",
                transform: "translateY(-50%)",
                backgroundColor: "rgba(255,255,255,0.7)",
                zIndex: 2,
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.9)",
                },
              }}
            >
              <ArrowBackIosNewIcon />
            </IconButton>
          )}

          {/* Right navigation arrow */}
          {currentCardIndex < carouselCards.length - 1 && (
            <IconButton
              onClick={() => handleCardNavigation("next")}
              sx={{
                position: "absolute",
                right: 0,
                top: "50%",
                transform: "translateY(-50%)",
                backgroundColor: "rgba(255,255,255,0.7)",
                zIndex: 2,
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.9)",
                },
              }}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          )}

          {/* Card media (image/video) */}
          {cardFileObj &&
            (isVideoCard ? renderVideo(cardFileObj) : renderImage(cardFileObj))}

          {/* Card body */}
          <Card
            variant="elevation"
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              width: "100%",
              backgroundColor: "#FFFFFF",
              padding: showSampleValues ? "7.12px" : "10px",
              mb: 1,
              "& p": {
                lineHeight: "1.5",
              },
            }}
          >
            {bodyPreview(
              replacePlaceholders(
                currentCard.body,
                currentCard.sampleBodyFields,
              ),
            )}
          </Card>

          {/* Card button */}
          {currentCard.buttons && currentCard.buttons.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                mb: 1,
              }}
            >
              {currentCard.buttons.map((button, index) => (
                <Card
                  key={index}
                  variant="elevation"
                  sx={{
                    width: "100%",
                    height: "30px",
                    minHeight: "30px",
                    backgroundColor: "#FFFFFF",
                    padding: "5.34103px 0px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <ReplyIcon sx={{ fontSize: 16, color: "primary.main" }} />
                  <Typography
                    align="center"
                    color={"#007DFF"}
                    fontSize={"14px"}
                    fontWeight={400}
                  >
                    {button.data.buttonText}
                  </Typography>
                </Card>
              ))}
            </Box>
          )}
        </Box>

        {/* Pagination indicators */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 2,
            gap: "6px",
          }}
        >
          {carouselCards.map((_, index) => (
            <Box
              key={index}
              sx={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor:
                  index === currentCardIndex
                    ? "#25D366"
                    : "rgba(255,255,255,0.5)",
                cursor: "pointer",
              }}
              onClick={() => handlePaginationClick(index)}
            />
          ))}
        </Box>
      </Box>
    );
  }

  const catalogPreview = () => {
    return (
      <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
        <Box>
          <Image
            sx={{ height: 80, width: 80, color: "grey", ml: -1, mb: -1 }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography fontWeight="bold">
            View {businessData?.business_name || "Business"}'s Catalog
          </Typography>
          <Typography variant="body2" color="grey">
            Browse pictures and details of their offerings.
          </Typography>
        </Box>
      </Box>
    );
  };

  const multipleProductsPreview = (headerText) => {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          gap: "1em"
        }}
      >
        <Box>
          {thumbnailImage ? (
            <Box
              component="img"
              src={thumbnailImage}
              alt="Thumbnail Product"
              sx={{
                height: 80,
                width: 80,
                ml: -1,
                mb: -1,
                borderRadius: 1,
                objectFit: "cover",
              }}
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "block";
              }}
            />
          ) : null}
          <Image
            sx={{
              height: 80,
              width: 80,
              color: "grey",
              ml: -1,
              mb: -1,
              display: thumbnailImage ? "none" : "block",
            }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography fontWeight="bold">
            {headerText || "View Products"}
          </Typography>
          <Typography variant="body2" color="grey">
            {productCount} items
          </Typography>
        </Box>
      </Box>
    );
  };

  const singleProductPreview = () => {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          gap: "1em"
        }}
      >
        <Box>
          {thumbnailImage ? (
            <Box
              component="img"
              src={thumbnailImage}
              alt="Product"
              sx={{
                height: 80,
                width: 80,
                ml: -1,
                mb: -1,
                borderRadius: 1,
                objectFit: "cover",
              }}
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "block";
              }}
            />
          ) : null}
          <Image
            sx={{
              height: 80,
              width: 80,
              color: "grey",
              ml: -1,
              mb: -1,
              display: thumbnailImage ? "none" : "block",
            }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography fontWeight="bold">
            {productName || "View Product"}
          </Typography>
        </Box>
      </Box>
    );
  };

  // Adjust paper and card width based on showSampleValues
  const contentWidth = "300px";

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          width: { xs: "340px", xxl: "500px" },
          maxWidth: "90vw",
          backgroundImage: mode === "view" ? `url(${waBackgroundImg})` : "none", //
          backgroundSize: "cover",
          backgroundPosition: "center",
          borderRadius: "20px",
          position: "sticky",
          top: "70px",
          height: "fit-content",
          minheight: "300px",
          maxHeight: "min(calc(100vh - 120px), 1000px)",
          overflow: "hidden",
        }}
      >
        {/* Header section*/}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            background: "#075E54",
            p: 2,
            borderRadius: "20px 20px 0px 0px",
          }}
        >
          <ArrowBack sx={{ color: "white", fontSize: 16 }} />
          <WhatsApp sx={{ color: "white", ml: 1 }} />
          <Typography sx={{ color: "white", ml: 1 }}>
            {userProfileData?.businessName || "Business"}
          </Typography>
          <Verified sx={{ color: "lime", fontSize: 16, ml: 1 }} />
          <Box sx={{ flexGrow: 1 }} />
          <MoreVert sx={{ color: "white" }} />
        </Box>

        {/* Message content container */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            width: "100%",
            backgroundColor: "#F5F5F5",
            backgroundImage: `url(${waBackgroundImg})`,
            padding: "10px",
            borderRadius: "0px 0px 20px 20px",
            height: "100%",
            gap: 2,
            flex: 1,
            overflowY: "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              overflowY: "auto",
              scrollbarColor: "#aaaaaaff transparent",
            }}
          >
            {/* Check if this is a carousel template */}
            {props.marketingOption === "carousel" ? (
              // Render carousel content
              <Box
                sx={{
                  width: "100%",
                  borderRadius: "8px",
                }}
              >
                {carouselPreview()}
              </Box>
            ) : props.marketingOption === "catalog" ? (
              <Box sx={{ marginBottom: 1 }}>
                <Card
                  variant="elevation"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    width: "100%",
                    backgroundColor: "#FFFFFF",
                    padding: showSampleValues ? "7.12px" : "10px",
                    "& p": {
                      lineHeight: "1.5",
                    },
                  }}
                >
                  {catalogPreview()}
                  {body !== "" ? bodyPreview(displayBodyText) : null}
                  {footer !== "" ? <>{footerPreview(footer)}</> : null}
                </Card>
              </Box>
            ) : props.marketingOption === "multiple-products" ? (
              <Box sx={{ marginBottom: 1 }}>
                <Card
                  variant="elevation"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    width: "100%",
                    backgroundColor: "#FFFFFF",
                    padding: showSampleValues ? "7.12px" : "10px",
                    "& p": {
                      lineHeight: "1.5",
                    },
                    marginBottom: 1,
                  }}
                >
                  {multipleProductsPreview(displayHeaderText)}
                  {body !== "" ? bodyPreview(displayBodyText) : null}
                  {footer !== "" ? <>{footerPreview(footer)}</> : null}
                </Card>
                {buttonPreview({
                  type: "custom",
                  data: {
                    buttonText: "View items",
                  },
                })}
              </Box>
            ) : props.marketingOption === "single-product" ? (
              <Box sx={{ marginBottom: 1 }}>
                <Card
                  variant="elevation"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    width: "100%",
                    backgroundColor: "#FFFFFF",
                    padding: showSampleValues ? "7.12px" : "10px",
                    "& p": {
                      lineHeight: "1.5",
                    },
                    marginBottom: 1,
                  }}
                >
                  {singleProductPreview()}
                  {body !== "" ? bodyPreview(displayBodyText) : null}
                  {footer !== "" ? <>{footerPreview(footer)}</> : null}
                </Card>
                {buttonPreview({
                  type: "custom",
                  data: {
                    buttonText: "View",
                  },
                })}
              </Box>
            ) : (
              // Render regular template content
              <Box sx={{ marginBottom: 1 }}>
                <Card
                  variant="elevation"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    width: "100%",
                    backgroundColor: "#FFFFFF",
                    padding: showSampleValues ? "7.12px" : "10px",
                    "& p": {
                      lineHeight: "1.5",
                    },
                  }}
                >
                  {headerType !== "none" ? (
                    <>
                      {headerPreview({
                        format: headerType,
                        str: displayHeaderText,
                        file: headerFile,
                      })}
                    </>
                  ) : null}
                  {body !== "" ? bodyPreview(displayBodyText) : null}
                  {footer !== "" ? <>{footerPreview(footer)}</> : null}
                </Card>
              </Box>
            )}

            {/* Button rendering logic - only show for regular templates */}
            {props.marketingOption !== "carousel" &&
              allButtons &&
              allButtons.length > 0 && (
                <>
                  {/* Show first 3 buttons */}
                  {allButtons
                    .slice(0, 3)
                    .map((button, index) => buttonPreview(button))}

                  {/* If more than 3 buttons exist, show 'See all options' */}
                  {allButtons.length > 3 && (
                    <Card
                      variant="elevation"
                      onClick={() => setShowAllButtons(true)}
                      sx={{
                        width: "100%",
                        height: "30px",
                        minHeight: "30px",
                        marginTop: "8px",
                        backgroundColor: "#FFFFFF",
                        padding: "5.34103px 0px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                        "&:hover": {
                          backgroundColor: "#F5F5F5",
                        },
                        marginBottom: 2,
                      }}
                    >
                      <ListIcon
                        sx={{ fontSize: 20, color: "primary.main" }}
                      ></ListIcon>
                      <Typography
                        align="center"
                        color={"#007DFF"}
                        fontSize={"14px"}
                        fontWeight={400}
                      >
                        See all options
                      </Typography>
                    </Card>
                  )}
                </>
              )}
          </Box>

          {/* Footer section*/}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              gap: 1,
              marginTop: "auto",
            }}
          >
            <Box
              sx={{
                flex: 1,
                borderRadius: 20,
                display: "flex",
                alignItems: "center",
                p: 1,
                backgroundColor: "white",
                gap: 1,
                height: 50,
              }}
            >
              <EmojiEmotions sx={{ color: "grey" }} />
              <Typography sx={{ flex: 1 }}>Message</Typography>
              <AttachFile sx={{ color: "grey" }} />
              <CameraAlt sx={{ color: "grey" }} />
            </Box>
            <Box
              sx={{
                background: "#075E54",
                borderRadius: 50,
                width: 50,
                height: 50,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Mic sx={{ color: "white" }} />
            </Box>
          </Box>

          {/* Slide-up modal - only for regular templates */}
          {props.marketingOption !== "carousel" &&
            createSlideUpModal({
              open: showAllButtons,
              onClose: () => setShowAllButtons(false),
              buttons: allButtons || [],
            })}
        </Box>
      </Paper>

      <Dialog
        open={!!formPreviewJson}
        onClose={() => setFormPreviewJson(null)}
        maxWidth={false}
      >
        <DialogContent sx={{ p: 3 }}>
          {formPreviewJson && <FormPreview formJson={formPreviewJson} />}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MessageNode;
